import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Text } from '@eo-locale/react';
import SearchDropDown from '../../Forms/partials/SearchDropDown';
import { defaultFailedResponse } from '@components/auth/fetchData';
import { trackIp } from '@utils';
import './InsurancePremiumFormStandalone.scss';

const insuranceConditions = {
    used: 'Used',
    new: 'New'
};

const vehicleCategories = {
    car: 'Car',
    bike: 'Bike',
    scooter: 'Scooter'
};

const categoryIdMap = {
    'car': '1',
    'bike': '2',
    'scooter': '3'
};

const DEFAULT_NEW_YEAR = '2026';

const INITIAL_FORM = {
    category: 'car',
    insurance_condition: 'used',
    make: '',
    model: '',
    year: '',
    trim: '',
    city: ''
};

const InsurancePremiumFormStandalone = () => {
    const ENV_WEB_URL = useSelector(state => state.initState.WEB_URL ?? '');
    const navigate = useNavigate();

    const [cities, setCities] = React.useState([]);
    const [makes, setMakes] = React.useState([]);
    const [models, setModels] = React.useState([]);
    const [years, setYears] = React.useState([]);
    const [trims, setTrims] = React.useState([]);

    const [insuranceForm, setInsuranceForm] = React.useState(INITIAL_FORM);

    const [showError, setShowError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [apiError, setApiError] = React.useState('');

    const isNewCondition = insuranceForm.insurance_condition === 'new';

    const resetVehicleFields = (overrides = {}) => {
        setMakes([]);
        setModels([]);
        setYears([]);
        setTrims([]);
        setInsuranceForm(prev => ({
            ...prev,
            make: '',
            model: '',
            year: '',
            trim: '',
            ...overrides
        }));
    };

    
    const handleCategoryChange = (key) => {
        setCities(prev => prev); 
        setMakes([]);
        setModels([]);
        setYears([]);
        setTrims([]);
        setInsuranceForm(prev => ({
            ...prev,
            category: key,
            make: '',
            model: '',
            year: '',
            trim: '',
            city: ''
        }));
    };

   
    const handleConditionChange = (key) => {
        // Do NOT clear makes — category hasn't changed and the effect won't re-fire
        setModels([]);
        setYears([]);
        setTrims([]);
        setInsuranceForm(prev => ({
            ...prev,
            insurance_condition: key,
            make: '',
            model: '',
            year: key === 'new' ? DEFAULT_NEW_YEAR : '',
            trim: ''
        }));
    };

    const handleMakeChange = (val, currentIsNew) => {
        setModels([]);
        setYears([]);
        setTrims([]);
        setInsuranceForm(prev => ({
            ...prev,
            make: val,
            model: '',
            year: currentIsNew ? DEFAULT_NEW_YEAR : '',
            trim: ''
        }));
    };

    const handleModelChange = (val, currentIsNew) => {
        setYears([]);
        setTrims([]);
        setInsuranceForm(prev => ({
            ...prev,
            model: val,
            year: currentIsNew ? DEFAULT_NEW_YEAR : '',
            trim: ''
        }));
    };

    const handleYearChange = (val) => {
        setTrims([]);
        setInsuranceForm(prev => ({ ...prev, year: val, trim: '' }));
    };

    
    React.useEffect(() => {
        if (!ENV_WEB_URL) return;
        fetch(`${ENV_WEB_URL}/get_city_list`)
            .then(res => res.json().catch(() => defaultFailedResponse))
            .then(data => {
                if (data.data) {
                    const options = Object.keys(data.data).map(
                        word => word.charAt(0).toUpperCase() + word.slice(1)
                    );
                    setCities(options);
                }
            })
            .catch(err => console.error('Error fetching cities:', err));
    }, [ENV_WEB_URL]);

    
    React.useEffect(() => {
        if (!insuranceForm.category || !ENV_WEB_URL) return;
        const params = new URLSearchParams({
            category_id: categoryIdMap[insuranceForm.category],
            api_version: 3
        }).toString();
        fetch(`${ENV_WEB_URL}/mmyt?${params}`)
            .then(res => res.json().catch(() => defaultFailedResponse))
            .then(data => {
                if (Array.isArray(data.data)) {
                    const filtered = [...new Set(data.data
                        .filter(item => {
                            if (typeof item === 'object' && item !== null) {
                                return item.selectable !== false;
                            }
                            return true;
                        })
                        .map(item => item?.name || item)
                        .filter(Boolean)
                    )];
                    setMakes(filtered);
                } else if (data.data?.make) {
                    setMakes(Object.keys(data.data.make));
                }
            })
            .catch(err => console.error('Error fetching makes:', err));
    }, [insuranceForm.category, ENV_WEB_URL]);

    React.useEffect(() => {
        if (!insuranceForm.category || !insuranceForm.make || !ENV_WEB_URL) return;
        const condition = insuranceForm.insurance_condition;
        const isNew = condition === 'new';
        const endpoint = isNew ? 'mmt_new' : 'mmyt';
        const params = new URLSearchParams({
            category_id: categoryIdMap[insuranceForm.category],
            make: insuranceForm.make,
            api_version: 3
        }).toString();
        fetch(`${ENV_WEB_URL}/${endpoint}?${params}`)
            .then(res => res.json().catch(() => defaultFailedResponse))
            .then(data => {
                let list = [];
                if (isNew) {
                 
                    if (Array.isArray(data.data)) {
                        list = data.data.map(item => item?.name || item).filter(Boolean);
                    }
                } else {
                    if (Array.isArray(data.data)) {
                        list = data.data.map(item => item?.name || item).filter(Boolean);
                    } else if (data.data?.make?.[insuranceForm.make]) {
                        list = Object.keys(data.data.make[insuranceForm.make]);
                    }
                }
                setModels(list);
            })
            .catch(err => console.error('Error fetching models:', err));
    }, [insuranceForm.category, insuranceForm.make, insuranceForm.insurance_condition, ENV_WEB_URL]);

    React.useEffect(() => {
        if (!insuranceForm.category || !insuranceForm.make || !insuranceForm.model || !ENV_WEB_URL) return;

       
        if (isNewCondition) return;

        const params = new URLSearchParams({
            category_id: categoryIdMap[insuranceForm.category],
            make: insuranceForm.make,
            model: insuranceForm.model,
            api_version: 3
        }).toString();
        fetch(`${ENV_WEB_URL}/mmyt?${params}`)
            .then(res => res.json().catch(() => defaultFailedResponse))
            .then(data => {
                let list = [];
                if (Array.isArray(data.data)) {
                    list = data.data.map(item => item?.name || item).filter(Boolean).sort((a, b) => b - a);
                } else if (data.data?.make?.[insuranceForm.make]?.[insuranceForm.model]) {
                    list = Object.keys(data.data.make[insuranceForm.make][insuranceForm.model]).sort((a, b) => b - a);
                }
                setYears(list);
            })
            .catch(err => console.error('Error fetching years:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insuranceForm.category, insuranceForm.make, insuranceForm.model, ENV_WEB_URL, insuranceForm.insurance_condition]);

    React.useEffect(() => {
        if (
            !insuranceForm.category ||
            !insuranceForm.make ||
            !insuranceForm.model ||
            !insuranceForm.year ||   
            !ENV_WEB_URL
        ) return;

        const isNew = insuranceForm.insurance_condition === 'new';
        const endpoint = isNew ? 'mmt_new' : 'mmyt';
        const params = new URLSearchParams({
            category_id: categoryIdMap[insuranceForm.category],
            make: insuranceForm.make,
            model: insuranceForm.model,
            ...(isNew ? {} : { year: insuranceForm.year }),
            check_obv: 1,
            api_version: 3
        }).toString();
        fetch(`${ENV_WEB_URL}/${endpoint}?${params}`)
            .then(res => res.json().catch(() => defaultFailedResponse))
            .then(data => {
                let list = [];
                if (isNew) {
                    
                    if (Array.isArray(data.data?.result)) {
                        list = data.data.result;
                    } else if (Array.isArray(data.data)) {
                        list = data.data.map(item => item?.name || item).filter(Boolean);
                    }
                } else {
                    if (Array.isArray(data.data)) {
                        list = data.data.map(item => item?.name || item).filter(Boolean);
                    } else if (data.data?.make?.[insuranceForm.make]?.[insuranceForm.model]?.[insuranceForm.year]) {
                        list = Object.keys(data.data.make[insuranceForm.make][insuranceForm.model][insuranceForm.year]);
                    } else if (data.data?.result) {
                        list = data.data.result;
                    }
                }
                setTrims(list);
                setInsuranceForm(prev => ({ ...prev, trim: '' }));
            })
            .catch(err => console.error('Error fetching trims:', err));
    }, [insuranceForm.category, insuranceForm.make, insuranceForm.model, insuranceForm.year, insuranceForm.insurance_condition, ENV_WEB_URL]);
//new comment 

    const handleSubmit = async () => {
        const { category, insurance_condition, make, model, year, trim, city } = insuranceForm;
        if (!category || !insurance_condition || !make || !model || !year || !trim || !city) {
            setShowError(true);
            setErrorMessage('Please fill all the fields');
            return;
        }
        setShowError(false);
        setErrorMessage('');
        setLoading(true);
        setApiError('');
        try {
            const payload = {
                insurance_condition: insurance_condition.toLowerCase(),
                make,
                model,
                year,
                trim,
                city: city.toLowerCase()
            };
            const response = await fetch(`${ENV_WEB_URL}/calculateinsurancepremium`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify(payload)
            });
            const responseText = await response.text();
            let data;
            try { data = JSON.parse(responseText); }
            catch (e) { throw new Error('API returned invalid JSON.'); }

            if (data.code === 'success' && data.data) {
                const categoryMap = { car: '1', bike: '2', scooter: '3' };
                const params = new URLSearchParams({
                    condition: insurance_condition.toLowerCase(),
                    categoryId: categoryMap[category] || '1',
                    make,
                    model,
                    year,
                    trim,
                    city: city.toLowerCase()
                }).toString();

                trackIp('check_price_insurance');

                navigate(`/insurance-result?${params}`);
            } else {
                setApiError(data.error || 'Failed to calculate insurance premium');
            }
        } catch (error) {
            console.error('Error:', error);
            setApiError('An error occurred while calculating the premium. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    

    return (
        <div className="block m-0">
            <div className="header">
                <h6 className="heading text-center">
                    <Text
                        id="Insurance_Premium_Calculator"
                        defaultMessage="Insurance Premium Calculator"
                    />
                </h6>
            </div>
            <div className="tab-content" id="insuranceTabContent">
                <div
                    className="tab-pane fade active show"
                    id="insurance"
                    role="tabpanel"
                    aria-labelledby="insurance-tab"
                >
                    <div className="content p-0">
                        <p>
                            <Text
                                id="Insurance_desc"
                                defaultMessage="Insurance cost estimate for your vehicle"
                            />
                        </p>
                        <div className="py-3 mt-2">
                            <div>
                                {/* Vehicle Type */}
                                <ul className="nav nav-pills nav-fill mb-3" role="tablist">
                                    {Object.entries(vehicleCategories).map(([key, label]) => (
                                        <li className="nav-item" key={key}>
                                            <a
                                                className={`nav-link ${insuranceForm.category === key ? 'active' : ''}`}
                                                onClick={(e) => { e.preventDefault(); handleCategoryChange(key); }}
                                                href="#"
                                                role="tab"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>

                                {/* Vehicle Condition */}
                                <ul className="user-category text-center obv-padding-left-0">
                                    {Object.entries(insuranceConditions).map(([key, label], idx) => (
                                        <li className="obv-display-inline-block" key={key} style={{ marginLeft: idx > 0 ? '30px' : '0' }}>
                                            <div className="form-group without-arrow">
                                                <div className="custom-control custom-radio">
                                                    <input
                                                        id={`condition-${key}`}
                                                        type="radio"
                                                        className="custom-control-input"
                                                        name="insurance_condition"
                                                        value={key}
                                                        checked={insuranceForm.insurance_condition === key}
                                                        onChange={() => handleConditionChange(key)}
                                                    />
                                                    <label
                                                        className="custom-control-label"
                                                        htmlFor={`condition-${key}`}
                                                    >
                                                        {label}
                                                    </label>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                               <div className="form-group">
                                    <SearchDropDown
        key={`make-${insuranceForm.category}`}
        options={makes}
        placeholder="Select Make"
        onSelect={(value) => handleMakeChange(value, isNewCondition)}
    />
</div>

                                {/* Model */}
                                <div className="form-group">
                                    <SearchDropDown
                                        key={`model-${insuranceForm.make}`}
                                        options={models}
                                        placeholder="Select Model"
                                        onSelect={(value) => handleModelChange(value, isNewCondition)}
                                    />
                                </div>

                                {/* Year — only shown for Used */}
                                {!isNewCondition && (
                                    <div className="form-group">
                                        <SearchDropDown
                                            key={`year-${insuranceForm.model}`}
                                            options={years}
                                            placeholder="Select Year"
                                            onSelect={(value) => handleYearChange(value)}
                                        />
                                    </div>
                                )}

                                {/* Trim */}
                                <div className="form-group">
                                    <SearchDropDown
                                        key={`trim-${insuranceForm.year}-${insuranceForm.model}`}
                                        options={trims}
                                        placeholder="Select Trim"
                                        onSelect={(value) =>
                                            setInsuranceForm(prev => ({ ...prev, trim: value }))
                                        }
                                    />
                                </div>

                                {/* City */}
                                <div className="form-group">
                                    <SearchDropDown
                                        key={`city-${insuranceForm.category}`}
                                        options={cities}
                                        placeholder="Select City"
                                        onSelect={(value) =>
                                            setInsuranceForm(prev => ({ ...prev, city: value }))
                                        }
                                    />
                                </div>

                                {showError && (
                                    <div className='text-center text-danger'>
                                        {errorMessage}
                                    </div>
                                )}
                                {apiError && (
                                    <div className='text-center text-danger'>
                                        {apiError}
                                    </div>
                                )}

                                <button
                                    className="btn btn-block btn-primary"
                                    id="check_price_insurance"
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? 'Calculating...' : 'Calculate Premium'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InsurancePremiumFormStandalone;